import React from 'react'

import { useHistory } from 'react-router-dom'

import { Empty, Select, Typography } from 'antd'

import { toJS } from 'mobx'
import { observer } from 'mobx-react-lite'

import { Box } from 'common/components/boxes'
import { Loading } from 'common/components/Loading'
import { OrdersList } from 'common/components/OrdersList'
import { Page } from 'common/components/Page'
import { PsqlTable } from 'common/components/PsqlTable'
import { ResponsiveTable } from 'common/components/ResponsiveTable'
import { RibbonFilter } from 'common/components/RibbonFilter'
import { SearchInput } from 'common/components/SearchInput'
import { usePersistentFilters } from 'common/contexts/persistent_filters'
import { useQuery } from 'common/hooks/use-query'
import { OrderStates, OrderSubStates } from 'common/server/server_types'

import { ConsolidatedOrders } from 'contractor/server/orders'

import { useVendorStores } from 'vendor/hooks/use-stores'
import { OrdersFilters } from 'vendor/pages/Private/@v2/Orders/components/orders_filter'

const allCustomersItem = {
  label: 'All customers',
}

const allOrdersItem = {
  label: 'All orders',
}

function Content() {
  const history = useHistory()

  const {
    notificationStore,
    userStore,
    orderStore: { listStore },
  } = useVendorStores()

  const persistentFilters = usePersistentFilters()

  React.useEffect(() => {
    persistentFilters.init()
  }, [])

  useQuery(() => {
    return Promise.all([listStore.fetchFacets()])
  }, [])

  function pushToOrder({ row }) {
    history.push(`order/${row.order_id}`)
  }

  function handleSearch(value: string) {
    persistentFilters.handleChangeFilters({ search: value }, false)
  }

  const renderMobileCard = React.useCallback((item: ConsolidatedOrders.Order) => {
    const notificationCount = notificationStore.notificationsCountByOrder(item.id)

    return (
      <OrdersList.MobileCard
        notificationCount={notificationCount}
        item={item}
        canSelectDelivery={false}
        showContractor
      />
    )
  }, [])

  return (
    <Page>
      <Page.Header display="flex" flexDirection="column" gridGap={16}>
        <Box display="flex" gridGap="16px" mx={-8}>
          <Select
            bordered={false}
            allowClear
            aria-label="Customer"
            placeholder="All customer"
            defaultValue={listStore.searchState.filters['company_name'] || allCustomersItem}
            value={listStore.searchState.filters['company_name']}
            style={{ minWidth: 300, fontSize: 18 }}
            onChange={(value) => persistentFilters.handleChangeFilters({ customer: value })}
            optionLabelProp="label"
            showArrow
          >
            <Select.Option value={undefined} label="All customers" style={{ minHeight: 35 }}>
              All customers
            </Select.Option>
            {(listStore.facets['companies'] || []).map((item, index) => {
              const label = item.company_name
              const value = item.company_name

              return (
                <Select.Option key={index} value={value} label={label} style={{ minHeight: 35 }}>
                  {label}
                </Select.Option>
              )
            })}
          </Select>

          {userStore.canUseOrdersFilters && (
            <Select
              bordered={false}
              allowClear
              aria-label="Orders"
              placeholder="Orders"
              defaultValue={listStore.searchState.filters['only_vendor_user_orders'] || allOrdersItem}
              value={listStore.searchState.filters['only_vendor_user_orders']}
              style={{ minWidth: 300, fontSize: 18 }}
              onChange={(value) => persistentFilters.handleChangeFilters({ only_vendor_user_orders: value })}
              showArrow
            >
              <Select.Option value={false} label="All orders" style={{ minHeight: 35 }}>
                All orders
              </Select.Option>
              <Select.Option value={true} label="My orders" style={{ minHeight: 35 }}>
                My orders
              </Select.Option>
            </Select>
          )}
        </Box>

        <Box display="flex" gridGap={8} alignItems="center" justifyContent="space-between">
          <SearchInput value={listStore.searchState.search} onSearch={handleSearch} />
          <OrdersFilters values={listStore.searchState.filters} facets={listStore.facets} />
        </Box>

        <Box width="100%" style={{ overflow: 'auto' }}>
          <RibbonFilter
            value={listStore.searchState.filters['state'] || []}
            counts={listStore.stateCounts || {}}
            onChange={(value) => persistentFilters.handleChangeFilters({ state: value })}
            showCounts
            options={[
              { label: 'All', filter: '' },
              { label: 'Quote Requested', filter: OrderSubStates.QUOTED_REQUESTED },
              { label: 'Quote Provided', filter: OrderSubStates.QUOTED_RECEIVED },
              { label: 'Order Requested', filter: OrderSubStates.ORDERED_REQUESTED },
              { label: 'Order Confirmed', filter: OrderSubStates.ORDERED_CONFIRMED },
              { label: 'Shipped', filter: OrderStates.SHIPPED },
              { label: 'Partially Shipped', filter: OrderStates.PARTIALLY_SHIPPED },
              { label: 'Delivered', filter: OrderStates.DELIVERED },
            ]}
            boxProps={{ overflowX: 'auto' }}
          />
        </Box>
      </Page.Header>

      <Page.Content p={0} px={{ _: 0, md: 12, lg: 16 }} pb={24} pt={{ _: 0, md: 16 }} height="100%">
        {listStore.isFetching && listStore.searchState.page === 1 ? (
          <Loading />
        ) : listStore.records.length === 0 ? (
          <Box
            px={{ _: 12, lg: 24 }}
            pb={{ _: 12, lg: 24 }}
            height="100%"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
          >
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Typography.Title level={5} style={{ textAlign: 'center' }}>
                  Sorry! No Items were found with these search criteria.
                </Typography.Title>
              }
            />
          </Box>
        ) : (
          <ResponsiveTable
            Table={PsqlTable}
            data={toJS(listStore.records) ?? []}
            tableName="AllOrders"
            hasMore={listStore.hasMore}
            loadMore={listStore.fetchNextPage}
            onLoadMore={listStore.fetchNextPage}
            renderMobileCard={renderMobileCard}
            onClickRow={pushToOrder}
          />
        )}
      </Page.Content>
    </Page>
  )
}

export const VendorOrdersPageContent = observer(Content)
